/**
 * Browser-side image prep for Marketplace listings, before upload: icons are
 * cropped to a square (same approach as agent logos in utils/agentPhotos.ts),
 * screenshots are scaled down. Raster images only — no SVG.
 */

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const ICON_PX = 256;
const SCREENSHOT_MAX_WIDTH = 1600;

function loadImage(file: File): Promise<{ img: HTMLImageElement; release: () => void }> {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return Promise.reject(new Error('Use a PNG, JPG or WebP image.'));
  }
  const url = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ img, release: () => URL.revokeObjectURL(url) });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Couldn't read that image."));
    };
    img.src = url;
  });
}

function draw(img: HTMLImageElement, sx: number, sy: number, sw: number, sh: number, w: number, h: number, type: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("This browser can't process images.");
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
  return canvas.toDataURL(type, 0.85);
}

/** Center-crops to a square and scales to 256px. PNG keeps transparent logos transparent. */
export async function iconFromFile(file: File): Promise<string> {
  const { img, release } = await loadImage(file);
  try {
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - side) / 2;
    const sy = (img.naturalHeight - side) / 2;
    return draw(img, sx, sy, side, side, ICON_PX, ICON_PX, 'image/png');
  } finally {
    release();
  }
}

/** Keeps the aspect ratio, scaled down to at most 1600px wide. */
export async function screenshotFromFile(file: File): Promise<string> {
  const { img, release } = await loadImage(file);
  try {
    const scale = Math.min(1, SCREENSHOT_MAX_WIDTH / img.naturalWidth);
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    return draw(img, 0, 0, img.naturalWidth, img.naturalHeight, w, h, 'image/jpeg');
  } finally {
    release();
  }
}
